import React, { useState, useEffect } from 'react';
import { BASE_URL } from './Constants';
import Header from './Header';
import Sidebar from './Sidebar';
import Footer from './Footer';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const AreaWiseShirtTrouser = () => {
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [areas, setAreas] = useState([]);
    const [selectedArea, setSelectedArea] = useState('');
    const [fullData, setFullData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [gender, setGender] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const recordsPerPage = 10;

    const token = localStorage.getItem('authToken');
    const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
    };

    const fetchAreas = async () => {
        try {
            const res = await fetch(`${BASE_URL}get_areas`, { headers });
            const data = await res.json();
            if (data.status) {
                setAreas(data.data);
            }
        } catch (err) {
            console.error('Error fetching areas:', err);
        }
    };

    const fetchPaymentData = async () => {
        setLoading(true);
        try {
            let url = `${BASE_URL}area_wise_shirt_trouser`;
            const params = [];
            if (fromDate) params.push(`from_date=${fromDate}`);
            if (toDate) params.push(`to_date=${toDate}`);
            if (selectedArea) params.push(`area=${selectedArea}`);
            if (gender) params.push(`gender=${gender}`);
            if (params.length > 0) url += `?${params.join('&')}`;

            const res = await fetch(url, { headers });
            const data = await res.json();
            if (data.status) {
                setFullData(data.data);
                setCurrentPage(1); // reset to first page
            } else {
                setFullData([]);
            }
        } catch (err) {
            console.error("Error fetching payment data:", err);
            setFullData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAreas();
    }, []);

    useEffect(() => {
        fetchPaymentData();
    }, [fromDate, toDate, selectedArea, gender]);

    const handleToggleSidebar = () => {
        if (window.innerWidth >= 992) {
            setIsSidebarOpen(prev => !prev);
        }
    };

    // Pagination logic
    const totalCount = fullData.length;
    const totalPages = Math.ceil(totalCount / recordsPerPage);
    const paginatedData = fullData.slice(
        (currentPage - 1) * recordsPerPage,
        currentPage * recordsPerPage
    );

    const getPageNumbers = () => {
        const pageLinks = [];
        const startPage = Math.max(1, currentPage - 2);
        const endPage = Math.min(totalPages, currentPage + 2);
        for (let i = startPage; i <= endPage; i++) {
            pageLinks.push(i);
        }
        return pageLinks;
    };

    const handleExportToExcel = () => {
        if (fullData.length === 0) {
            alert("No data to export.");
            return;
        }

        const dataToExport = fullData.map((item, index) => ({
            '#': index + 1,
            'Area Name': item.area,
            'Dress Type': item.dress_type,
            'Dress Size': item.dress_size,
            'Gender': item.gender,
            'Consumed Quantity': item.uniform_qty,
        }));

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Consumed Report");

        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const fileData = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(fileData, "Consumed_Report.xlsx");
    };

    const totalConsumedQty = fullData.reduce((sum, item) => sum + Number(item.uniform_qty || 0), 0);



    return (
        <div className="page-wrapper">
            <Sidebar isOpen={isSidebarOpen} />
            <div className="page-container" style={{ marginLeft: isSidebarOpen ? '300px' : '100px' }}>
                <Header onToggleSidebar={handleToggleSidebar} />
                <div className="main-content">
                    <div className="section__content section__content--p30">
                        <div className="container-fluid">
                            <h4 className="mb-4">Date Wise Consumed Report</h4>

                            <div className="row mb-3">
                                <div className="col-md-3">
                                    <input type="date" className="form-control" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                                </div>
                                <div className="col-md-3">
                                    <input type="date" className="form-control" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                                </div>
                                <div className="col-md-2">
                                    <select className="form-control" value={selectedArea} onChange={(e) => setSelectedArea(e.target.value)}>
                                        <option value="">Select Area</option>
                                        {areas.map((area) => (
                                            <option key={area.id} value={area.id}>{area.area}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <select className="form-control" value={gender} onChange={(e) => setGender(e.target.value)}>
                                        <option value="">All Genders</option>
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <button className="btn btn-success" onClick={handleExportToExcel}>
                                        Export to Excel
                                    </button>
                                </div>
                            </div>

                            <div className="table-responsive">
                                <table className="table table-bordered table-striped">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Area Name</th>
                                            <th>Dress Type</th>
                                            <th>Dress Size</th>
                                            <th>Gender</th>
                                            <th>Consumed Quantity (Total:{totalConsumedQty})</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedData.length > 0 ? (
                                            paginatedData.map((item, index) => (
                                                <tr key={index}>
                                                    <td>{(currentPage - 1) * recordsPerPage + index + 1}</td>
                                                    <td>{item.area}</td>
                                                    <td>{item.dress_type}</td>
                                                    <td>{item.dress_size}</td>
                                                    <td>{item.gender}</td>
                                                    <td>{item.uniform_qty}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="6" className="text-center">No data found.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div className="d-flex justify-content-between align-items-center mt-3">
                                <div><strong>Total Records: {totalCount}</strong></div>
                                <nav>
                                    <ul className="pagination mb-0">
                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}>
                                                &lt;
                                            </button>
                                        </li>
                                        {getPageNumbers().map((page) => (
                                            <li key={page} className={`page-item ${page === currentPage ? 'active' : ''}`}>
                                                <button className="page-link" onClick={() => setCurrentPage(page)}>{page}</button>
                                            </li>
                                        ))}
                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}>
                                                &gt;
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            </div>

                            {loading && <div className="mt-3 text-center">Loading...</div>}
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        </div>
    );
};

export default AreaWiseShirtTrouser;
